import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle, Save, XCircle } from 'lucide-react';

const COLORS = {
  navyBlue: '#003366', orange: '#FF6B35', mustard: '#FFAA00', green: '#16A34A', red: '#DC2626',
  lightGray: '#F9FAFB', white: '#FFFFFF', borderGray: '#D1D5DB', textGray: '#6B7280', textDark: '#1F2937',
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 60000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: { backgroundColor: COLORS.navyBlue, color: COLORS.white, padding: '8px 16px', borderBottom: `2px solid ${COLORS.mustard}` },
  headerContent: { maxWidth: '1180px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backButton: { backgroundColor: 'transparent', border: 'none', color: COLORS.white, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '8px 0' },
  body: { maxWidth: '1180px', margin: '0 auto', padding: '24px' },
  card: { backgroundColor: COLORS.white, borderRadius: '10px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', padding: '24px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '650', color: COLORS.textDark, marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '7px', fontSize: '15px', boxSizing: 'border-box', backgroundColor: COLORS.white },
  select: { width: '100%', padding: '10px 12px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '7px', fontSize: '15px', boxSizing: 'border-box', backgroundColor: COLORS.white },
  button: { backgroundColor: COLORS.orange, color: COLORS.white, padding: '12px 24px', borderRadius: '7px', fontWeight: '700', border: 'none', cursor: 'pointer', fontSize: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' },
  errorBox: { backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '12px 16px', borderRadius: '7px', marginBottom: '16px' },
};

const errorMapToArray = (map) => Object.entries(map)
  .filter(([, value]) => value != null)
  .map(([itemNumber, value]) => ({ item_number: Number(itemNumber), student_response: String(value || '').trim() }))
  .sort((a, b) => a.item_number - b.item_number);

const trimErrorsToLast = (map, lastItem) => {
  const max = Number(lastItem || 0);
  const next = {};
  Object.entries(map).forEach(([k, v]) => {
    if (Number(k) <= max) next[k] = v;
  });
  return next;
};

function SubtestPanel({ title, subtitle, subtest, lastItem, setLastItem, errors, setErrors }) {
  const items = subtest?.items || [];
  const attempted = useMemo(() => items.filter((item) => item.number <= Number(lastItem || 0)), [items, lastItem]);
  const errorNumbers = useMemo(() => new Set(Object.keys(errors).map(Number)), [errors]);
  const errorCount = [...errorNumbers].filter((n) => n <= Number(lastItem || 0)).length;
  const rawScore = lastItem ? Number(lastItem) - errorCount : null;

  const changeLast = (value) => {
    setLastItem(value);
    setErrors((prev) => trimErrorsToLast(prev, value));
  };

  const toggleError = (item) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (Object.prototype.hasOwnProperty.call(next, String(item.number))) delete next[String(item.number)];
      else next[String(item.number)] = '';
      return next;
    });
  };

  return <div style={styles.card}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
      <div>
        <h2 style={{ color: COLORS.navyBlue, fontSize: '20px', margin: '0 0 4px', textAlign: 'left' }}>{title}</h2>
        <div style={{ color: COLORS.textGray, fontSize: '13px' }}>{subtitle}</div>
      </div>
      {lastItem && <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '8px', padding: '8px 12px', minWidth: '108px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: COLORS.textGray }}>Raw score</div>
          <div style={{ fontSize: '22px', color: COLORS.green, fontWeight: '800' }}>{rawScore}</div>
        </div>
        <div style={{ backgroundColor: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: '8px', padding: '8px 12px', minWidth: '90px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: COLORS.textGray }}>Errors</div>
          <div style={{ fontSize: '22px', color: '#C2410C', fontWeight: '800' }}>{errorCount}</div>
        </div>
      </div>}
    </div>

    <div style={{ maxWidth: '520px', marginBottom: '18px' }}>
      <label style={styles.label}>Last word read in 45 seconds *</label>
      <select style={styles.select} value={lastItem} onChange={(e) => changeLast(e.target.value)}>
        <option value="">Select the last word reached</option>
        {items.map((item) => <option key={item.number} value={item.number}>{item.number}. {item.target}</option>)}
      </select>
    </div>

    {lastItem && <>
      <div style={{ fontSize: '13px', color: COLORS.textGray, marginBottom: '10px', textAlign: 'left' }}>Click only the words read incorrectly. Unmarked attempted words are treated as correct.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(138px, 1fr))', gap: '8px', marginBottom: errorCount ? '18px' : '4px' }}>
        {attempted.map((item) => {
          const isError = errorNumbers.has(item.number);
          return <button key={item.number} type="button" onClick={() => toggleError(item)} aria-pressed={isError} style={{
            minHeight: '48px', borderRadius: '8px', padding: '7px 10px', cursor: 'pointer', textAlign: 'left',
            border: isError ? '2px solid #EF4444' : '1px solid #CBD5E1',
            backgroundColor: isError ? '#FEF2F2' : COLORS.white, color: COLORS.textDark,
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <span style={{ fontSize: '10px', color: COLORS.textGray, minWidth: '22px' }}>{item.number}</span>
            <span style={{ fontSize: '15px', fontWeight: isError ? '700' : '500', flex: 1 }}>{item.target}</span>
            {isError && <XCircle size={17} color={COLORS.red} />}
          </button>;
        })}
      </div>

      {errorCount > 0 && <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '14px' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: COLORS.navyBlue, marginBottom: '10px', textAlign: 'left' }}>Error evidence</div>
        <div style={{ display: 'grid', gap: '10px' }}>
          {attempted.filter((item) => errorNumbers.has(item.number)).map((item) => <div key={item.number} style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 220px) 1fr', gap: '12px', alignItems: 'center', backgroundColor: '#FAFAFA', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ textAlign: 'left' }}><span style={{ color: COLORS.textGray, fontSize: '11px', marginRight: '7px' }}>{item.number}</span><strong>{item.target}</strong></div>
            <input style={styles.input} value={errors[String(item.number)] || ''} onChange={(e) => setErrors((prev) => ({ ...prev, [String(item.number)]: e.target.value }))} placeholder="How the student read it (optional)" />
          </div>)}
        </div>
      </div>}
    </>}
  </div>;
}

export default function TOWRE2Screening({ student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, onBack, onSaved }) {
  const [config, setConfig] = useState(null);
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [notes, setNotes] = useState('');
  const [sightLastItem, setSightLastItem] = useState('');
  const [nonrealLastItem, setNonrealLastItem] = useState('');
  const [sightErrors, setSightErrors] = useState({});
  const [nonrealErrors, setNonrealErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stage, setStage] = useState('form');
  const [result, setResult] = useState(null);
  const timepoint = initialTimepoint || 'Baseline';

  useEffect(() => {
    let active = true;
    fetchWithTimeout(`${apiUrl}/api/towre2/config`)
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!active) return;
        if (!ok) throw new Error(data.error || 'Could not load timed reading configuration');
        setConfig(data);
      })
      .catch((e) => { if (active) setError(e.message || 'Could not load timed reading configuration'); });
    return () => { active = false; };
  }, [apiUrl]);

  const sight = config?.subtests?.sight;
  const nonreal = config?.subtests?.nonreal;

  const submit = async () => {
    if (!sightLastItem || !nonrealLastItem) {
      setError('Please select the last word reached for both 45-second lists.');
      return;
    }
    setError(''); setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/towre2/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code, screening_date: screeningDate, timepoint,
          sight_last_item: Number(sightLastItem), sight_errors: errorMapToArray(sightErrors),
          nonreal_last_item: Number(nonrealLastItem), nonreal_errors: errorMapToArray(nonrealErrors),
          school_id: schoolId || student?.school_id || '', school_name: schoolName || '',
          programme_id: programmeId || student?.programme_id || '', programme_name: programmeName || '',
          assessor_name: assessorName, administration_mode: 'Individual',
          entered_by_user_id: facilitatorId, entered_by_name: facilitatorName, notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save timed reading administration');
      setResult(data); setStage('complete'); if (onSaved) onSaved(data);
    } catch (e) {
      if (e?.name === 'AbortError') setError('The save took longer than 60 seconds. Check Screening Sessions before trying again.');
      else setError(e.message || 'Could not save timed reading administration');
    } finally { setLoading(false); }
  };

  const Header = ({ title }) => <div style={styles.header}><div style={styles.headerContent}>
    <button style={styles.backButton} onClick={onBack}><ArrowLeft size={18} /> Back to {student.full_name}</button>
    <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
  </div></div>;

  if (stage === 'complete') {
    const scoring = result?.scoring || {};
    return <div style={styles.container}><Header title="Timed Single Word Reading: Saved" /><div style={styles.body}><div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '18px' }}><CheckCircle size={28} /><h2 style={{ fontSize: '20px', margin: 0 }}>Saved</h2></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '14px', marginBottom: '18px' }}>
        {[scoring.sight, scoring.nonreal].filter(Boolean).map((s) => <div key={s.code} style={{ border: '1px solid #D1D5DB', borderRadius: '9px', padding: '16px', textAlign: 'left' }}>
          <div style={{ fontSize: '13px', color: COLORS.textGray, marginBottom: '8px' }}>{s.label}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(70px, 1fr))', gap: '10px', marginBottom: '10px' }}>
            <div><div style={{ fontSize: '11px', color: COLORS.textGray }}>Raw score</div><div style={{ fontSize: '25px', fontWeight: '800', color: COLORS.navyBlue }}>{s.raw_score}</div></div>
            <div><div style={{ fontSize: '11px', color: COLORS.textGray }}>Standard score</div><div style={{ fontSize: '25px', fontWeight: '800', color: COLORS.navyBlue }}>{s.standard_score ?? '-'}</div></div>
            <div><div style={{ fontSize: '11px', color: COLORS.textGray }}>Percentile</div><div style={{ fontSize: '25px', fontWeight: '800', color: COLORS.navyBlue }}>{s?.patoss?.percentile_display ?? '-'}</div></div>
          </div>
          {s?.patoss?.descriptor && <div style={{ fontSize: '12px', fontWeight: '700', color: COLORS.textDark, marginBottom: '5px' }}>{s.patoss.descriptor}</div>}
          <div style={{ fontSize: '13px', color: COLORS.textDark }}>Last word: {s.last_item}. {s.last_target}</div>
          <div style={{ fontSize: '12px', color: COLORS.textGray, marginTop: '3px' }}>{s.error_count} error{s.error_count === 1 ? '' : 's'} in {s.attempted_count} attempted</div>
        </div>)}
      </div>
      <button style={{ ...styles.button, backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `2px solid ${COLORS.navyBlue}` }} onClick={onBack}>Back to {student.full_name}</button>
    </div></div></div>;
  }

  return <div style={styles.container}><Header title="Timed Single Word Reading" /><div style={styles.body}>
    <div style={styles.card}>
      <h1 style={{ fontSize: '22px', color: COLORS.navyBlue, margin: '0 0 5px', textAlign: 'left' }}>Timed Single Word Reading - {student.full_name}</h1>
      <div style={{ color: COLORS.textGray, fontSize: '13px', marginBottom: '16px', textAlign: 'left' }}>Form B · 45 seconds per list · Timepoint: {timepoint}</div>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
        <div><label style={styles.label}>Screening date *</label><input style={styles.input} value={screeningDate} onChange={(e) => setScreeningDate(e.target.value)} /></div>
        <div><label style={styles.label}>Administration</label><div style={{ ...styles.input, backgroundColor: '#F8FAFC' }}>Individual</div></div>
        <div><label style={styles.label}>Assessor</label><input style={styles.input} value={assessorName} onChange={(e) => setAssessorName(e.target.value)} /></div>
      </div>
    </div>

    {sight && <SubtestPanel title="Sight Recognition Words" subtitle="45 seconds" subtest={sight} lastItem={sightLastItem} setLastItem={setSightLastItem} errors={sightErrors} setErrors={setSightErrors} />}
    {nonreal && <SubtestPanel title="Non-real Words" subtitle="45 seconds" subtest={nonreal} lastItem={nonrealLastItem} setLastItem={setNonrealLastItem} errors={nonrealErrors} setErrors={setNonrealErrors} />}

    <div style={styles.card}>
      <label style={styles.label}>Notes (optional)</label>
      <textarea style={{ ...styles.input, minHeight: '78px', fontFamily: 'inherit' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <button style={{ ...styles.button, opacity: loading ? 0.65 : 1 }} onClick={submit} disabled={loading}><Save size={18} /> {loading ? 'Saving...' : 'Submit & Lock'}</button>
        <span style={{ fontSize: '12px', color: COLORS.textGray }}>Only error items are written to detailed Screening Responses.</span>
      </div>
    </div>
  </div></div>;
}
